import { supabase } from '../../../shared/lib/supabase';

// Define the UserOrganisation interface based on your database schema
export interface UserOrganisation {
  id: string;
  user_id: string;
  organisation_id: string;
  role: string;
  active: boolean;
  created_at: string;
  updated_at: string;
  // Joined fields
  user_email?: string;
  user_name?: string;
  organisation_name?: string;
}

export interface UserOrganisationCreateInput {
  user_id: string;
  organisation_id: string;
  role: string;
  active?: boolean;
}

export interface UserOrganisationUpdateInput {
  role?: string;
  active?: boolean;
}

class UserOrganisationsService {
  async getAll(): Promise<UserOrganisation[]> {
    try {
      console.log('🔍 Fetching user organisations...');
      
      const { data, error } = await supabase
        .from('user_organisations')
        .select(`
          *,
          organisations!inner(name),
          users!inner(first_name,last_name,email)
        `)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('❌ Error fetching user organisations:', error);
        throw error;
      }

      // Transform the data to include joined fields
      const transformedData = data?.map((item: any) => ({
        ...item,
        user_email: item.users?.email || '',
        user_name: item.users ? `${item.users.first_name || ''} ${item.users.last_name || ''}`.trim() : '',
        organisation_name: item.organisations?.name || ''
      })) || [];

      console.log('✅ User organisations fetched successfully:', transformedData.length, 'records');
      return transformedData;
    } catch (error) {
      console.error('❌ User organisations fetch error:', error);
      throw error;
    }
  }

  async getById(id: string): Promise<UserOrganisation | null> {
    try {
      console.log(`🔍 Fetching user organisation by ID: ${id}`);
      
      const { data, error } = await supabase
        .from('user_organisations')
        .select('*')
        .eq('id', id)
        .single(); // Return single record instead of array

      if (error) {
        console.error('❌ Error fetching user organisation:', error);
        throw error;
      }

      console.log('✅ User organisation fetched successfully:', data);
      return data;
    } catch (error) {
      console.error('❌ User organisation fetch error:', error);
      throw error;
    }
  }

  async create(data: UserOrganisationCreateInput): Promise<UserOrganisation> {
    try {
      console.log('📝 Creating user organisation:', data);
      
      const { data: result, error } = await supabase
        .from('user_organisations')
        .insert([data])
        .select() // Return the inserted record with generated fields
        .single();

      if (error) {
        console.error('❌ Error creating user organisation:', error);
        throw error;
      }

      console.log('✅ User organisation created successfully:', result);
      return result;
    } catch (error) {
      console.error('❌ User organisation creation error:', error);
      throw error;
    }
  }

  async update(id: string, data: UserOrganisationUpdateInput): Promise<UserOrganisation> {
    try {
      console.log(`📝 Updating user organisation ${id}:`, data);
      
      // Add updated_at timestamp
      const dataWithTimestamp = {
        ...data,
        updated_at: new Date().toISOString()
      };

      const { data: result, error } = await supabase
        .from('user_organisations')
        .update(dataWithTimestamp)
        .eq('id', id)
        .select()
        .single();

      if (error) {
        console.error('❌ Error updating user organisation:', error);
        throw error;
      }

      console.log('✅ User organisation updated successfully:', result);
      return result;
    } catch (error) {
      console.error('❌ User organisation update error:', error);
      throw error;
    }
  }

  async delete(id: string): Promise<void> {
    try {
      console.log(`🗑️ Deleting user organisation: ${id}`);
      
      const { error } = await supabase
        .from('user_organisations')
        .delete()
        .eq('id', id);

      if (error) {
        console.error('❌ Error deleting user organisation:', error);
        throw error;
      }

      console.log('✅ User organisation deleted successfully');
    } catch (error) {
      console.error('❌ User organisation deletion error:', error);
      throw error;
    }
  }
}

export const userOrganisationsService = new UserOrganisationsService();
